import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Microphone, Stop, CircleNotch } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const VoiceMode = ({ threadId, onClose }) => {
  const [status, setStatus] = useState("idle"); // idle|rec|think|speak
  const [lastQ, setLastQ] = useState("");
  const [lastA, setLastA] = useState("");
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const stRef = useRef("idle");
  stRef.current = status;

  useEffect(() => () => {
    try { if (recRef.current?.state === "recording") recRef.current.stop(); } catch {}
    audioRef.current?.pause();
  }, []);

  const start = async () => {
    try {
      audioRef.current?.pause();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setStatus("think");
        try {
          const fd = new FormData();
          fd.append("file", new Blob(chunks, { type: mime }), "v.webm");
          const { data } = await api.post("/voice/transcribe", fd);
          setLastQ(data.text);
          const es = new EventSource(`${BASE}/api/ai/chat/stream?q=${encodeURIComponent(data.text)}${threadId ? `&thread_id=${threadId}` : ""}`, { withCredentials: true });
          let full = "";
          es.onmessage = async (e) => {
            try {
              const d = JSON.parse(e.data);
              if (d.t) { full += d.t; setLastA(full); }
              if (d.done) {
                es.close();
                const clean = full.replace(/\s*\[\[[^\]]*\]\]/g, "").trim();
                if (!clean) { setStatus("idle"); return; }
                setStatus("speak");
                const r = await fetch(`${BASE}/api/ai/tts`, { method: "POST", credentials: "include",
                  headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: clean }) });
                if (r.ok) {
                  const audio = new Audio(URL.createObjectURL(await r.blob()));
                  audio.onended = () => setStatus("idle");
                  audioRef.current = audio;
                  audio.play().catch(() => setStatus("idle"));
                } else setStatus("idle");
              }
              if (d.error) { es.close(); setStatus("idle"); }
            } catch {}
          };
          es.onerror = () => { es.close(); setStatus("idle"); };
        } catch { setStatus("idle"); }
      };
      rec.start();
      recRef.current = rec;
      setStatus("rec");
    } catch { setStatus("idle"); }
  };

  const tap = () => {
    if (stRef.current === "idle") start();
    else if (stRef.current === "rec") recRef.current?.stop();
    else if (stRef.current === "speak") { audioRef.current?.pause(); setStatus("idle"); }
  };

  const color = status === "rec" ? "#ff3b30" : status === "think" ? "#ffcf3f" : status === "speak" ? "#00e676" : "#0084ff";
  const label = { idle: "Нажми и говори", rec: "Слушаю… нажми — стоп", think: "Думаю…", speak: "Говорю — нажми, чтобы прервать" }[status];

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-6" data-testid="voice-mode">
      <button onClick={onClose} data-testid="voice-mode-close" className="absolute top-5 right-5 p-2 text-muted-foreground hover:text-foreground"><X size={24} /></button>
      <button onClick={tap} data-testid="voice-mode-circle"
        className="w-40 h-40 rounded-full flex items-center justify-center transition-all active:scale-95"
        style={{ border: `4px solid ${color}`, boxShadow: `0 0 60px ${color}66`,
                 animation: status === "rec" || status === "speak" ? "micPulseVm 1.2s ease-in-out infinite" : "none" }}>
        {status === "think" ? <CircleNotch size={52} color={color} className="animate-spin" />
          : status === "rec" ? <Stop size={52} color={color} weight="fill" />
          : <Microphone size={52} color={color} weight="fill" />}
      </button>
      <style>{`@keyframes micPulseVm { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }`}</style>
      <p className="mt-6 text-lg font-semibold" style={{ color }} data-testid="voice-mode-status">{label}</p>
      {lastQ && <p className="mt-4 text-sm text-muted-foreground italic text-center max-w-md line-clamp-2">«{lastQ}»</p>}
      {lastA && <p className="mt-2 text-sm text-foreground/90 text-center max-w-md max-h-40 overflow-y-auto whitespace-pre-wrap">{lastA.replace(/\s*\[\[[^\]]*\]\]/g, "")}</p>}
    </div>
  );
};
